import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { AlertCircle, Loader } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import insurancePolicyService from "../../lib/InsurancePolicyService";
import RenewalInsurancePoliciesView from "./RenewalPoliciesView";

const RenewalPolicyUploader = ({ onUpload, onSync, onComplete, onBack }) => {
  const { user, getIdToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingPolicies, setExistingPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const handlePolicySelect = (policy) => {
    setSelectedPolicy(policy);
  };

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        if (!user || !user.uid) {
          setError("User authentication required");
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        const token = await getIdToken();

        // Use existing policy service
        const result = await insurancePolicyService.checkExpiringPolicies(
          token
        );

        if (result) {
          setExistingPolicies(result);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error checking existing policies:", err);
        setError("Unable to check for existing policies. Please try again.");
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [user]);

  const handleProceed = () => {
    if (onComplete && existingPolicies && existingPolicies.length > 0) {
      // Store the selected policy in session storage
      if (selectedPolicy) {
        sessionStorage.setItem(
          "selectedRenewalPolicy",
          JSON.stringify(selectedPolicy)
        );
      }
      onComplete();
    }
  };

  // Existing loading and error states remain the same
  if (loading) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <h1 className="text-xl uppercase font-bold">
            Checking Renewal Opportunities
          </h1>
          <p className="text-center text-darkGrey !mt-6">
            This should only take a moment
          </p>
          {/* Existing loading spinner */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Something Went Wrong
          </h1>
          <div className="flex items-center justify-center text-red-600">
            <AlertCircle className="w-6 h-6 mr-2" />
            <span>{error}</span>
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // When policies are found
  if (existingPolicies && existingPolicies.length > 0) {
    return (
      <div>
        <RenewalInsurancePoliciesView
          policies={existingPolicies}
          onReupload={(policy) => {
            onUpload(policy);
          }}
          onPolicySelect={handlePolicySelect}
        />
        <div className="space-y-6 p-4">
          <button
            onClick={handleProceed}
            className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors ${
              !selectedPolicy ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={!selectedPolicy}
          >
            <span>Next</span>
          </button>
          {/* <button
            onClick={onBack}
            className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors !mt-4"
          >
            Back
          </button> */}
        </div>
      </div>
    );
  }

  // Default view if no policies found
  return (
    <div className="space-y-6 p-4">
      <div className="text-center space-y-4">
        <h1 className="text-xl uppercase font-bold text-gray-900">
          No Expiring Policies Found
        </h1>
        <p className="text-gray-600">
          We do not have any policies that are expiring. To create your Ownli
          Insurance Profile{" "}
          <a
            href="https://insurance.ownli.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            click here
          </a>
          .
        </p>
      </div>

      {/* <div className="flex flex-col">
        <button
          onClick={onBack}
          className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors mt-4"
        >
          Back
        </button>
      </div> */}
    </div>
  );
};

export default RenewalPolicyUploader;
